// src/contexts/UserProvider.js
import React, { useState, useEffect } from 'react';
import UserContext from './UserContext';
import { useAuth } from './AuthProvider';

const USER_STORAGE_KEY = 'user-session';

const UserProvider = ({ children }) => {
  const { api } = useAuth();

  const [user, setUser] = useState(() => {
    // On initial load, try to rehydrate user state from localStorage
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to persist user info in localStorage
  const persistUser = (userData) => {
    if (userData) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  };

  // Login: Calls /api/login, updates user state and persists in localStorage
  const login = async (credentials) => {
    try {
      const response = await api.post('/api/login', credentials);
      if (response.data?.user) {
        setUser(response.data.user);
        persistUser(response.data.user);
      }
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  // Signup: Similar to login
  const signup = async (userData) => {
    try {
      const response = await api.post('/api/signup', userData);
      if (response.data?.user) {
        setUser(response.data.user);
        persistUser(response.data.user);
      }
      return response.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  };

  //  Forgot Password: send reset link to email**
  const forgotPassword = async (email) => {
    try {
      const response = await api.post(
        '/api/auth/forgot-password',
        { email },
      );
      return response.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  };

  // Reset password with provided token and new password
  const resetPassword = async (token, newPassword) => {
    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        newPassword,
      });
      return response.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  };

  // Logout: Clear user state and localStorage
  const logout = async () => {
    try {
      await api.post('/api/logout');
      setUser(null);
      persistUser(null);
      setError(null);
    } catch (err) {
      setError('Logout failed. Please try again.');
      throw err;
    }
  };

  const updateUser = async (payload) => {
    try {
      // Replace with your real API endpoint and logic!
      const res = await api.put('/api/user/update', payload); // Adjust endpoint as needed
      if (res.data && res.data.user) {
        setUser(res.data.user);
        persistUser(res.data.user);
        return res.data.user;
      }
      throw new Error(res.data?.error || 'Unknown error updating profile');
    } catch (err) {
      throw err.response?.data || err;
    }
  };

  // On mount, check for an existing session via /api/getMe
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/getMe');
        if (response.data?.user) {
          setUser(response.data.user);
          persistUser(response.data.user);
        } else {
          // No authenticated user from the server -> clear any cached session
          setUser(null);
          persistUser(null);
        }
        setError(null);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          // If not authenticated, clear stored user information
          setUser(null);
          persistUser(null);
        } else {
          // Failed to fetch user
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [api]);

  const userContextValue = {
    user,
    isAuthenticated: !!user,
    setUser: (userData) => {
      setUser(userData);
      persistUser(userData);
    },
    login,
    signup,
    logout,
    error,
    forgotPassword,
    resetPassword,
    setError,
    loading,
    updateUser,
  };

  return (
    <UserContext.Provider value={userContextValue}>
      {!loading ? children : <div>Loading...</div>}
      {error && <div className="error">{error}</div>}
    </UserContext.Provider>
  );
};

export default UserProvider;
